import React, { Fragment, useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import SEO from "../../components/SEO/SEO";
import ComparisonTable, { MatrixStatus } from "../../components/ComparisonTable/ComparisonTable";
import cardBg from "../../images/bg/pricing-bg01.png";
import serviceBg from "../../images/bg/pricing-bg.png";
import { rememberVerticalIntent } from "../../utils/verticalIntent";

// Comparison slug configuration — must stay in sync with COMPARISON_SLUGS in
// prerender.js and the comparisonData.* keys in every locale file.
const COMPARISON_CONFIG: Record<string, { i18nKey: string; relatedIndustrySlug: string; heroIcon: string }> = {
  petpooja: { i18nKey: "petpooja", relatedIndustrySlug: "restaurant", heroIcon: "fa-utensils" },
  rentok: { i18nKey: "rentok", relatedIndustrySlug: "hotel", heroIcon: "fa-hotel" },
  ezee: { i18nKey: "ezee", relatedIndustrySlug: "hotel", heroIcon: "fa-hotel" },
  hotelogix: { i18nKey: "hotelogix", relatedIndustrySlug: "hotel", heroIcon: "fa-hotel" },
  cloudbeds: { i18nKey: "cloudbeds", relatedIndustrySlug: "hotel", heroIcon: "fa-hotel" },
  zenoti: { i18nKey: "zenoti", relatedIndustrySlug: "salon-spa", heroIcon: "fa-spa" },
  mindbody: { i18nKey: "mindbody", relatedIndustrySlug: "gym-fitness", heroIcon: "fa-dumbbell" },
  selldo: { i18nKey: "selldo", relatedIndustrySlug: "real-estate", heroIcon: "fa-home" },
  practo: { i18nKey: "practo", relatedIndustrySlug: "healthcare", heroIcon: "fa-hospital" },
  teachmint: { i18nKey: "teachmint", relatedIndustrySlug: "education", heroIcon: "fa-graduation-cap" },
};

const pageStyles = `
  .comparison-hero {
    background: linear-gradient(135deg, rgba(0,255,151,0.08), rgba(0,0,0,0));
    border-bottom: 1px solid rgba(0,255,151,0.15);
  }
  .comparison-hero-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(0,255,151,0.2), rgba(0,255,151,0.05));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    color: var(--color-primary);
  }
  .comparison-category-badge {
    display: inline-block;
    background: rgba(0,255,151,0.08);
    border: 1px solid rgba(0,255,151,0.2);
    color: var(--color-primary);
    font-size: 12px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .comparison-table-wrap {
    overflow-x: auto;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,0.08);
  }
  .comparison-table {
    width: 100%;
    border-collapse: collapse;
    background: rgba(15, 20, 40, 0.6);
  }
  .comparison-table th,
  .comparison-table td {
    padding: 16px 20px;
    text-align: left;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    vertical-align: top;
  }
  .comparison-table thead th {
    background: rgba(0,255,151,0.06);
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--color-primary);
  }
  .comparison-table tbody th {
    font-weight: 500;
    opacity: 0.9;
    min-width: 220px;
  }
  .comparison-table-status {
    font-weight: 600;
    white-space: nowrap;
  }
  .comparison-table-note {
    font-size: 12px;
    opacity: 0.65;
    margin-top: 4px;
    max-width: 220px;
  }
  .comparison-leads-card,
  .comparison-gaps-card {
    background: rgba(15, 20, 40, 0.6);
    border-radius: 16px;
    padding: 30px;
    height: 100%;
  }
  .comparison-leads-card {
    border: 1px solid rgba(0,255,151,0.25);
  }
  .comparison-gaps-card {
    border: 1px solid rgba(255,107,107,0.25);
  }
  .comparison-leads-card li,
  .comparison-gaps-card li {
    margin-bottom: 16px;
    opacity: 0.9;
    line-height: 1.6;
  }
  .comparison-faq-item {
    border-bottom: 1px solid rgba(255,255,255,0.08);
    padding: 24px 0;
  }
  .comparison-faq-item:last-child {
    border-bottom: none;
  }
  .comparison-cta-section {
    background-image: url(${cardBg});
    border-radius: 20px;
  }
`;

const ComparisonPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const config = slug ? COMPARISON_CONFIG[slug] : undefined;
  const base = config ? `comparisonData.${config.i18nKey}` : "";

  const competitorName = useMemo(() => (config ? t(`${base}.competitorName`) : ""), [config, base, t]);
  const category = useMemo(() => (config ? t(`${base}.category`) : ""), [config, base, t]);
  const metaTitle = useMemo(() => (config ? t(`${base}.metaTitle`) : ""), [config, base, t]);
  const metaDescription = useMemo(() => (config ? t(`${base}.metaDescription`) : ""), [config, base, t]);
  const definition = useMemo(() => (config ? t(`${base}.definition`) : ""), [config, base, t]);
  const executiveSummary = useMemo(() => (config ? t(`${base}.executiveSummary`) : ""), [config, base, t]);
  const verdict = useMemo(() => (config ? t(`${base}.verdict`) : ""), [config, base, t]);
  const lastUpdated = useMemo(() => (config ? t(`${base}.lastUpdated`) : ""), [config, base, t]);

  const splitPipe = (value: string) => (value ? value.split("|").map((s) => s.trim()).filter(Boolean) : []);

  const matrixCapabilities = useMemo(() => (config ? splitPipe(t(`${base}.matrixCapabilities`, { defaultValue: "" })) : []), [config, base, t]);
  const matrixAxoixStatus = useMemo(() => (config ? splitPipe(t(`${base}.matrixAxoixStatus`, { defaultValue: "" })) as MatrixStatus[] : []), [config, base, t]);
  const matrixAxoixNote = useMemo(() => (config ? splitPipe(t(`${base}.matrixAxoixNote`, { defaultValue: "" })) : []), [config, base, t]);
  const matrixCompetitorStatus = useMemo(() => (config ? splitPipe(t(`${base}.matrixCompetitorStatus`, { defaultValue: "" })) as MatrixStatus[] : []), [config, base, t]);
  const matrixCompetitorNote = useMemo(() => (config ? splitPipe(t(`${base}.matrixCompetitorNote`, { defaultValue: "" })) : []), [config, base, t]);

  const whereAxoixLeads = useMemo(() => (config ? splitPipe(t(`${base}.whereAxoixLeads`, { defaultValue: "" })) : []), [config, base, t]);
  const honestGaps = useMemo(() => (config ? splitPipe(t(`${base}.honestGaps`, { defaultValue: "" })) : []), [config, base, t]);
  const faqQuestions = useMemo(() => (config ? splitPipe(t(`${base}.faqQuestions`, { defaultValue: "" })) : []), [config, base, t]);
  const faqAnswers = useMemo(() => (config ? splitPipe(t(`${base}.faqAnswers`, { defaultValue: "" })) : []), [config, base, t]);

  // Somebody comparing us to Petpooja runs a restaurant. Remember that, so the
  // signup form preselects it even if they leave via the header CTA rather than
  // the ?vertical= link further down this page. Must sit above the !config
  // early return — hooks cannot be conditional.
  useEffect(() => {
    rememberVerticalIntent(config?.relatedIndustrySlug);
  }, [config]);

  if (!config) {
    return <Navigate to="/404" replace />;
  }

  const relatedIndustrySlug = config.relatedIndustrySlug;
  // industriesData keys don't always match the URL slug 1:1 (e.g. "gym-fitness" -> "gym").
  const RELATED_INDUSTRY_I18N_KEY: Record<string, string> = {
    "real-estate": "realEstate",
    "salon-spa": "salonSpa",
    "gym-fitness": "gym",
  };
  const relatedIndustryTitle = t(`industriesData.${RELATED_INDUSTRY_I18N_KEY[relatedIndustrySlug] || relatedIndustrySlug}.title`, { defaultValue: relatedIndustrySlug });

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `AXOIX vs ${competitorName}`,
    description: metaDescription,
    author: {
      "@type": "Organization",
      name: "AXOIX TECHNOLOGIES PRIVATE LIMITED",
      url: "https://axoix.com",
    },
    publisher: {
      "@type": "Organization",
      name: "AXOIX TECHNOLOGIES PRIVATE LIMITED",
      url: "https://axoix.com",
    },
    datePublished: lastUpdated,
    dateModified: lastUpdated,
    url: `https://axoix.com/compare/${slug}`,
    mainEntityOfPage: `https://axoix.com/compare/${slug}`,
  };

  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqQuestions.map((question, idx) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faqAnswers[idx] || "",
      },
    })),
  };

  return (
    <Fragment>
      <SEO
        title={metaTitle}
        description={metaDescription}
        path={`/compare/${slug}`}
        schema={[articleSchema, faqPageSchema]}
      />
      <style>{pageStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle={`AXOIX vs ${competitorName}`} pagesub="Compare" headingLevel="h2" />

            {/* Hero */}
            <section className="comparison-hero pt-80 pb-60">
              <div className="container">
                <div className="row align-items-center">
                  <div className="col-lg-9">
                    <div className="d-flex align-items-center gap-4 mb-25">
                      <div className="comparison-hero-icon">
                        <i className={`fas ${config.heroIcon}`}></i>
                      </div>
                      <div>
                        <h1 className="mb-10">AXOIX vs {competitorName}</h1>
                        <span className="comparison-category-badge">{category}</span>
                      </div>
                    </div>
                    <p className="opacity-90 mb-0" style={{ fontSize: "1.15rem", lineHeight: 1.7 }}>
                      {definition}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Executive Summary */}
            <section className="pt-60 pb-40">
              <div className="container">
                <p className="opacity-80" style={{ fontSize: "1.05rem", lineHeight: 1.8, maxWidth: "900px" }}>
                  {executiveSummary}
                </p>
              </div>
            </section>

            {/* Comparison Table */}
            <section className="pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <h2 className="title">Feature-by-Feature Comparison</h2>
                </div>
                <ComparisonTable
                  competitorName={competitorName}
                  capabilities={matrixCapabilities}
                  axoixStatus={matrixAxoixStatus}
                  axoixNote={matrixAxoixNote}
                  competitorStatus={matrixCompetitorStatus}
                  competitorNote={matrixCompetitorNote}
                />
              </div>
            </section>

            {/* Where Axoix Leads / Honest Gaps */}
            <section className="pt-20 pb-60" style={{ backgroundImage: `url(${serviceBg})`, backgroundSize: "cover" }}>
              <div className="container">
                <div className="row">
                  <div className="col-lg-6 mb-30 mb-lg-0">
                    <div className="comparison-leads-card">
                      <h3 className="mb-20"><i className="fas fa-circle-check me-2" style={{ color: "var(--color-primary)" }}></i>Where AXOIX Leads</h3>
                      <ul className="list-unstyled">
                        {whereAxoixLeads.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="comparison-gaps-card">
                      <h3 className="mb-20"><i className="fas fa-triangle-exclamation me-2" style={{ color: "#ff6b6b" }}></i>Honest Gaps</h3>
                      <ul className="list-unstyled">
                        {honestGaps.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Verdict */}
            <section className="pt-60 pb-40">
              <div className="container">
                <h2 className="title mb-20">Bottom Line</h2>
                <p className="opacity-85" style={{ fontSize: "1.05rem", lineHeight: 1.8, maxWidth: "900px" }}>
                  {verdict}
                </p>
              </div>
            </section>

            {/* FAQ */}
            <section className="pt-40 pb-60">
              <div className="container">
                <div className="text-center mb-40">
                  <h2 className="title">Frequently Asked Questions</h2>
                </div>
                <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                  {faqQuestions.map((question, idx) => (
                    <div className="comparison-faq-item" key={idx}>
                      <h3 className="mb-10" style={{ fontSize: "1.1rem" }}>{question}</h3>
                      <p className="opacity-80 mb-0">{faqAnswers[idx]}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Cross-link to related industry page */}
            <section className="pb-60">
              <div className="container text-center">
                <Link to={`/industry/${relatedIndustrySlug}`} className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                  <span className="text"><i className="fas fa-arrow-left me-2"></i>See the full AXOIX {relatedIndustryTitle} vertical</span>
                </Link>
              </div>
            </section>

            {/* CTA */}
            <section className="pt-20 pb-100">
              <div className="container">
                <div className="text-center pricing-item xb-border bg_img p-50 comparison-cta-section">
                  <h2 className="mb-15">Ready to Switch from {competitorName}?</h2>
                  <p className="opacity-75 mb-30" style={{ maxWidth: "600px", margin: "0 auto 30px" }}>
                    Sign up for the free Explorer plan today. No credit card required.
                  </p>
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to={`/signup?vertical=${relatedIndustrySlug}`} className="thm-btn agency-btn">
                      <span className="text">Get Started Free</span>
                    </Link>
                    <Link to="/pricing" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                      <span className="text">View Pricing</span>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ComparisonPage;
